import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import './NovaPOS.css';

const NovaPOS = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    const webImages = [
        'mpos/web/Screenshot (739).png',
        'mpos/web/Screenshot (740).png',
        'mpos/web/Screenshot (742).png',
        'mpos/web/Screenshot (743).png',
        'mpos/web/Screenshot (744).png',
        'mpos/web/Screenshot (745).png',
        'mpos/web/Screenshot (746).png',
        'mpos/web/Screenshot (747).png',
        'mpos/web/Screenshot (748).png',
        'mpos/web/Screenshot (749).png',
        'mpos/web/Screenshot 2026-07-18 210923.png'
    ];

    const mobileImages = [
        'mpos/mobile/WhatsApp Image 2026-07-19 at 21.45.13 (1).jpeg',
        'mpos/mobile/WhatsApp Image 2026-07-19 at 21.45.13.jpeg',
        'mpos/mobile/WhatsApp Image 2026-07-19 at 21.45.14 (1).jpeg',
        'mpos/mobile/WhatsApp Image 2026-07-19 at 21.45.14.jpeg',
        'mpos/mobile/WhatsApp Image 2026-07-19 at 21.45.15.jpeg',
        'mpos/mobile/WhatsApp Image 2026-07-19 at 21.45.16 (1).jpeg',
        'mpos/mobile/WhatsApp Image 2026-07-19 at 21.45.16.jpeg',
        'mpos/mobile/WhatsApp Image 2026-07-19 at 21.45.17 (1).jpeg',
        'mpos/mobile/WhatsApp Image 2026-07-19 at 21.45.17 (2).jpeg',
        'mpos/mobile/WhatsApp Image 2026-07-19 at 21.45.17.jpeg'
    ];
    const techStack = [
        { name: 'Flutter', icon: '📱' },
        { name: 'React.js', icon: '⚛️' },
        { name: 'Node.js', icon: '🟩' },
        { name: 'Express.js', icon: '🚀' },
        { name: 'MySQL', icon: '🐬' },
        { name: 'Sequelize', icon: '🗃️' },
        { name: 'JWT', icon: '🔑' },
        { name: 'Firebase', icon: '🔥' },
        { name: 'Multer', icon: '📁' },
      ];

    const KeyFeatures = [
        'Intelligent Product Search',
        'Integrated Barcode Scanner',
        'Complete Inventory & Warehouse Management',
        'Sales Processing & Order Management',
        'Customer & Credit Management',
        'Tax & Discount Engine',
        'Multiple Payment Methods',
        'Hold Orders & Resume Sales',
        'Batch & Expiry Management',
        'User & Role Management',
        'Multi-Branch Support',
        'Store & Receipt Customization',
        'Live Business Dashboard',
        'Advanced Reporting & Analytics',
        'Secure Authentication & Authorization',
    ]

    return (
        <section className="bg-gray-50 py-12 px-6 md:px-12" id="novapos">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: -20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="mb-8 flex flex-col items-center justify-between gap-4 sm:flex-row sm:items-center"
                >
                    <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="text-4xl font-bold text-gray-900 text-center"
                    >
                        🚀 Project Showcase: NovaPOS
                        <motion.span
                            initial={{ width: 0 }}
                            animate={isInView ? { width: '100%' } : { width: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="block h-1 bg-gray-900 mt-2 mx-auto"
                        ></motion.span>
                    </motion.h2>
                </motion.div>
                <div className="text-gray-600 mb-8">
                I'm excited to share NovaPOS, a complete Full-Stack Point of Sale (POS) System that I developed to streamline retail operations through a scalable backend, a modern web administration panel, and a cross-platform mobile application. The system is designed to help businesses efficiently manage sales, inventory, customers, employees, reporting, and multiple store branches from a single platform.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">📱 Mobile POS Application (Flutter)</h3>
                        <ul className="list-disc list-inside text-gray-600">
                            <li>Fast and intuitive checkout experience</li>
                            <li>Barcode scanning</li>
                            <li>Smart product search (Name, SKU & Barcode)</li>
                            <li>Cart management</li>
                            <li>Hold & Resume Orders</li>
                            <li>Multiple payment methods (Cash, Card & Credit)</li>
                            <li>Digital PDF receipts</li>
                            <li>Dashboard with live sales analytics</li>
                            <li>Inventory management</li>
                            <li>Customer & credit management</li>
                            <li>Push notifications (Firebase Cloud Messaging)</li>
                            <li>Light & Dark Theme support</li>
                        </ul>
                    </div>
                    <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">💻 Web Administration Panel (React.js)</h3>
                        <ul className="list-disc list-inside text-gray-600">
                        <li>Business dashboard with real-time analytics</li>
                        <li>Sales performance reports & charts</li>
                        <li>Product, category, brand & supplier management</li>
                        <li>Inventory & warehouse management</li>
                        <li>Batch & expiry tracking</li>
                        <li>Bulk product import (Excel/CSV)</li>
                        <li>Customer management</li>
                        <li>Tax & discount configuration</li>
                        <li>User, Role & Branch management</li>
                        <li>Store profile & receipt customization</li>
                        <li>Comprehensive reporting suite</li>
                        </ul>
                    </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-800 mb-4">✨ Key Features</h3>
                <div className="flex flex-wrap gap-2 mb-8">
                    {KeyFeatures.map((feature, index) => (
                        <span key={index} className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full text-sm">✅ {feature}</span>
                    ))}
                </div>

                <h3 className="text-2xl font-bold text-gray-800 mb-4">🛠️ Technology Stack</h3>
                <div className="flex flex-wrap gap-4 mb-8">
                    {techStack.map((tech, index) => (
                        <div key={index} className="flex items-center bg-white p-3 rounded-lg shadow-md">
                            <span className="text-2xl mr-2">{tech.icon}</span>
                            <span className="font-semibold">{tech.name}</span>
                        </div>
                    ))}
                </div>


                <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Web App Screenshots</h3>
                <div className="slider-container mb-8">
                    <div className="image-slider">
                        {webImages.map((image, index) => (
                            <img key={index} src={image} alt={`NovaPOS web screenshot ${index + 1}`} className="rounded-lg shadow-lg" />
                        ))}
                    </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Mobile App Screenshots</h3>
                <div className="slider-container">
                    <div className="image-slider">
                        {mobileImages.map((image, index) => (
                            <img key={index} src={image} alt={`NovaPOS mobile screenshot ${index + 1}`} className="rounded-lg shadow-lg" />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export default NovaPOS;
